/**
 * Shared color palettes for artwork generation
 * Used by SonicGeneratePage, SoundByteGeneratePage, and PlaygroundPage
 */

export interface CuratedColor {
  id: string;
  name: string;
  hex: string;
}

export interface ColorCategory {
  label: string;
  colors: CuratedColor[];
}

/**
 * Organized color palette with categories for admin color selection
 */
export const COLOR_CATEGORIES: Record<string, ColorCategory> = {
  neutrals: {
    label: 'Neutrals',
    colors: [
      { id: 'black', name: 'Black', hex: '#000000' },
      { id: 'charcoal', name: 'Charcoal', hex: '#2C2C2C' },
      { id: 'gray', name: 'Gray', hex: '#6B7280' },
      { id: 'silver', name: 'Silver', hex: '#C0C0C0' },
      { id: 'white', name: 'White', hex: '#FFFFFF' },
    ],
  },
  blues: {
    label: 'Blues',
    colors: [
      { id: 'navy', name: 'Navy', hex: '#1E3A5F' },
      { id: 'royal-blue', name: 'Royal Blue', hex: '#1E40AF' },
      { id: 'ocean', name: 'Ocean', hex: '#0077B6' },
      { id: 'teal', name: 'Teal', hex: '#0D6E6E' },
      { id: 'sky', name: 'Sky Blue', hex: '#00B4D8' },
    ],
  },
  greens: {
    label: 'Greens',
    colors: [
      { id: 'forest', name: 'Forest', hex: '#1D4E3E' },
      { id: 'emerald', name: 'Emerald', hex: '#047857' },
      { id: 'sage', name: 'Sage', hex: '#4A7C59' },
      { id: 'lime', name: 'Lime', hex: '#65A30D' },
      { id: 'mint', name: 'Mint', hex: '#98D8C8' },
    ],
  },
  reds: {
    label: 'Reds & Pinks',
    colors: [
      { id: 'crimson', name: 'Crimson', hex: '#DC143C' },
      { id: 'ruby', name: 'Ruby', hex: '#E31837' },
      { id: 'burgundy', name: 'Burgundy', hex: '#722F37' },
      { id: 'rose', name: 'Rose', hex: '#FFB6C1' },
      { id: 'pink', name: 'Pink', hex: '#FF69B4' },
    ],
  },
  purples: {
    label: 'Purples',
    colors: [
      { id: 'purple', name: 'Purple', hex: '#5E3A87' },
      { id: 'violet', name: 'Violet', hex: '#9B59B6' },
      { id: 'lavender', name: 'Lavender', hex: '#E6E6FA' },
      { id: 'plum', name: 'Plum', hex: '#6B2D5B' },
      { id: 'indigo', name: 'Indigo', hex: '#4B0082' },
    ],
  },
  warm: {
    label: 'Warm Tones',
    colors: [
      { id: 'gold', name: 'Gold', hex: '#D4AF37' },
      { id: 'bronze', name: 'Bronze', hex: '#B87333' },
      { id: 'orange', name: 'Orange', hex: '#FF6B35' },
      { id: 'terracotta', name: 'Terracotta', hex: '#A0522D' },
      { id: 'sand', name: 'Sand', hex: '#D2B48C' },
    ],
  },
};

/**
 * Flat lookup map of all curated colors by ID
 */
export const CURATED_COLORS: Record<string, CuratedColor> = Object.values(COLOR_CATEGORIES)
  .flatMap(cat => cat.colors)
  .reduce((acc, color) => ({ ...acc, [color.id]: color }), {});

/**
 * Get all colors as a flat array
 */
export const getAllColors = (): CuratedColor[] => {
  return Object.values(COLOR_CATEGORIES).flatMap(cat => cat.colors);
};

/**
 * Get color by ID
 */
export const getColorById = (id: string): CuratedColor | undefined => {
  return CURATED_COLORS[id];
};

/**
 * Get color by hex value
 */
export const getColorByHex = (hex: string): CuratedColor | undefined => {
  const normalizedHex = hex.toUpperCase();
  return getAllColors().find(c => c.hex.toUpperCase() === normalizedHex);
};
